import { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRestoreJobPolling } from '../hooks/useRestoreJobPolling';
import { formatBytes } from '@/shared/utils/formatBytes';
import { RestoreJobLogsViewer } from './RestoreJobLogsViewer';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  FileCheck,
  HardDrive,
  Zap,
  X,
  ScrollText,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { getStatusTone } from '@shared/config/theme';
import { toneChipClasses, toneTextClasses } from '@shared/utils/statusTone';

interface RestoreProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  restoreJobId: string | null;
}

export const RestoreProgressModal = ({
  isOpen,
  onClose,
  restoreJobId,
}: RestoreProgressModalProps) => {
  const { t } = useTranslation('restores');
  const {
    status,
    progress,
    currentFile,
    filesRestored,
    dirsCreated,
    totalFiles,
    bytesWritten,
    error,
    duration,
    isLoading,
    startPolling,
    stopPolling,
    reset,
    getElapsedTime,
  } = useRestoreJobPolling();

  const [showLogsViewer, setShowLogsViewer] = useState(false);

  // Start polling when modal opens with a job ID
  useEffect(() => {
    if (isOpen && restoreJobId) {
      startPolling(restoreJobId);
    }

    return () => {
      // Unconditional on purpose: this cleanup closes over the PREVIOUS
      // render's isOpen, so a `if (!isOpen)` guard never fires on the paths
      // that matter (close while open, unmount while open). stopPolling is
      // idempotent and startPolling re-arms when the modal reopens.
      stopPolling();
    };
  }, [isOpen, restoreJobId, startPolling, stopPolling]);

  const handleClose = useCallback(() => {
    onClose();

    if (status === 'COMPLETED' || status === 'FAILED') {
      reset();
    }
  }, [onClose, reset, status]);

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  /** Glyph per polled state; the colour comes from the shared tone contract. */
  const statusIcons: Record<string, LucideIcon> = {
    COMPLETED: CheckCircle2,
    FAILED: XCircle,
    VERIFYING: FileCheck,
    RUNNING: Loader2,
    PENDING: Clock,
  };

  if (!isOpen || !restoreJobId) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200">
      <div className="bg-surface-card-light dark:bg-surface-card-dark rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col border border-surface-border-light dark:border-surface-border-dark overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-surface-border-light dark:border-surface-border-dark">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {(() => {
                const StatusIcon = statusIcons[status ?? ''] ?? Clock;
                return (
                  <StatusIcon
                    className={`w-6 h-6 ${toneTextClasses[getStatusTone(status)]} ${
                      status === 'VERIFYING'
                        ? 'animate-pulse'
                        : status === 'RUNNING'
                          ? 'animate-spin'
                          : ''
                    }`}
                  />
                );
              })()}
              <div>
                <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                  Restore Progress
                </h2>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-black uppercase tracking-wider mt-1 ${toneChipClasses[getStatusTone(status)]}`}
                >
                  {status || 'Loading...'}
                </span>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-600 dark:hover:text-slate-200 hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-xl transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 text-brand-600 dark:text-brand-400 animate-spin mb-4" />
              <p className="text-text-muted-light dark:text-text-muted-dark font-medium">
                Loading restore progress...
              </p>
            </div>
          )}

          {/* Progress Section */}
          {!isLoading && status !== 'FAILED' && (
            <>
              {/* Main Progress Bar */}
              <div>
                <div className="flex justify-between items-end mb-2">
                  <span className="text-sm font-bold text-slate-700 dark:text-slate-300">
                    {status === 'PENDING'
                      ? 'Waiting in queue...'
                      : status === 'VERIFYING'
                        ? 'Verifying file integrity...'
                        : status === 'COMPLETED'
                          ? 'Restore complete!'
                          : 'Restoring files...'}
                  </span>
                  <span className="text-2xl font-black text-brand-600 dark:text-brand-400">
                    {progress}%
                  </span>
                </div>
                <div className="w-full bg-surface-border-light dark:bg-surface-border-dark rounded-full h-4 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500 ease-out relative overflow-hidden bg-brand-600 dark:bg-brand-400"
                    style={{ width: `${progress}%` }}
                  >
                    {status === 'RUNNING' && (
                      <div className="absolute inset-0 bg-white/20 animate-pulse" />
                    )}
                  </div>
                </div>
              </div>

              {/* Current File */}
              {currentFile && (
                <div className="bg-surface-2-light dark:bg-surface-2-dark rounded-xl p-4 border border-surface-border-light dark:border-surface-border-dark">
                  <div className="flex items-center gap-2 text-xs text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider mb-2">
                    <HardDrive className="w-3.5 h-3.5" />
                    Currently Processing
                  </div>
                  <div className="font-mono text-sm text-slate-700 dark:text-slate-300 truncate">
                    {currentFile}
                  </div>
                </div>
              )}

              {/* Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <StatCard
                  icon={FileCheck}
                  label="Files"
                  value={`${filesRestored}${totalFiles > 0 ? `/${totalFiles}` : ''}`}
                  color="brand"
                />
                <StatCard icon={Zap} label="Speed" value="-" color="amber" />
                <StatCard
                  icon={HardDrive}
                  label="Transferred"
                  value={bytesWritten ? formatBytes(bytesWritten) : '-'}
                  color="emerald"
                />
                <StatCard
                  icon={Clock}
                  label={duration ? 'Duration' : 'Elapsed'}
                  value={duration ? formatDuration(duration) : formatDuration(getElapsedTime())}
                  color="purple"
                />
              </div>
            </>
          )}

          {/* Error State */}
          {status === 'FAILED' && error && (
            <div className="bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-2 border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-state-danger-bg-light dark:bg-state-danger-bg-dark rounded-xl">
                  <XCircle className="w-6 h-6 text-state-danger-light dark:text-state-danger-dark" />
                </div>
                <div>
                  <h3 className="font-bold text-state-danger-light dark:text-state-danger-dark text-lg">
                    Restore Failed
                  </h3>
                  <p className="text-state-danger-light dark:text-state-danger-dark mt-2">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Success State */}
          {status === 'COMPLETED' && (
            <div className="bg-state-ok-bg-light dark:bg-state-ok-bg-dark border-2 border-state-ok-light/20 dark:border-state-ok-dark/20 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-state-ok-bg-light dark:bg-state-ok-bg-dark rounded-xl">
                  <CheckCircle2 className="w-6 h-6 text-state-ok-light dark:text-state-ok-dark" />
                </div>
                <div>
                  <h3 className="font-bold text-state-ok-light dark:text-state-ok-dark text-lg">
                    Restore Completed Successfully
                  </h3>
                  <p className="text-state-ok-light dark:text-state-ok-dark mt-2">
                    {dirsCreated > 0
                      ? t('selective.progress.filesRestoredSuccessWithDirs', {
                          count: filesRestored,
                          dirs: dirsCreated,
                        })
                      : t('selective.progress.filesRestoredSuccess', { count: filesRestored })}{' '}
                    {duration ? `(${formatDuration(duration)})` : ''}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-5 border-t border-surface-border-light dark:border-surface-border-dark bg-surface-2-light dark:bg-surface-2-dark">
          <div className="flex justify-between items-center">
            {/* View Logs Button - Show when job is running or completed */}
            {restoreJobId && status !== 'PENDING' && (
              <button
                onClick={() => setShowLogsViewer(true)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-black uppercase tracking-wider text-slate-600 dark:text-text-subtle-dark hover:text-brand-600 dark:hover:text-brand-400 hover:bg-surface-2-light dark:hover:bg-surface-border-dark rounded-xl transition-colors"
              >
                <ScrollText className="w-4 h-4" />
                View Logs
              </button>
            )}
            <div className="flex gap-3 ml-auto">
              {status === 'COMPLETED' || status === 'FAILED' ? (
                <button
                  onClick={handleClose}
                  className="px-6 py-2.5 bg-brand-600 text-white text-sm font-black uppercase tracking-wider rounded-xl hover:bg-brand-700 transition-colors"
                >
                  {status === 'COMPLETED' ? 'Done' : 'Close'}
                </button>
              ) : (
                <button
                  onClick={handleClose}
                  className="px-6 py-2.5 text-sm font-black uppercase tracking-wider text-text-muted-light dark:text-text-muted-dark hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                >
                  Continue in Background
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Logs Viewer Modal */}
        <RestoreJobLogsViewer
          isOpen={showLogsViewer}
          onClose={() => setShowLogsViewer(false)}
          restoreJobId={restoreJobId || ''}
        />
      </div>

      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
};

// Stat Card Component
interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: string;
  color: 'brand' | 'amber' | 'emerald' | 'purple';
}

const StatCard = ({ icon: Icon, label, value, color }: StatCardProps) => {
  const colorClasses = {
    brand: 'bg-accent-soft-light text-brand-700 dark:bg-accent-soft-dark dark:text-brand-400',
    amber:
      'bg-state-warn-bg-light text-state-warn-light dark:bg-state-warn-bg-dark dark:text-state-warn-dark',
    emerald:
      'bg-state-ok-bg-light text-state-ok-light dark:bg-state-ok-bg-dark dark:text-state-ok-dark',
    purple:
      'bg-surface-2-light text-text-muted-light dark:bg-surface-2-dark dark:text-text-muted-dark',
  };

  return (
    <div className={`rounded-xl p-4 ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 opacity-70" />
        <span className="text-xs font-black uppercase tracking-wider opacity-70">{label}</span>
      </div>
      <div className="text-lg font-black">{value}</div>
    </div>
  );
};
